import { useState, useMemo } from "react"
import PageLayout from "components/layout/PageLayout"
import AvatarMini from "components/commons/AvatarMini"
import WarningMessage from "components/commons/WarningMessage"
import { useGetBadgeEventsPagedQuery } from "./api/attendanceApiSlice"
import { useGetBusinessUnitsQuery } from "features/core/businessUnit/api/businessUnitApiSlice"
import { useTranslation } from "react-i18next"
import { fromUnixTime, format } from "date-fns"
import { it, enUS } from "date-fns/locale"

const PAGE_SIZE = 20

function dateStringToEpoch(dateStr) {
    if (!dateStr) return undefined
    return Math.floor(new Date(dateStr + "T00:00:00").getTime() / 1000)
}

function dateStringToEpochEnd(dateStr) {
    if (!dateStr) return undefined
    return Math.floor(new Date(dateStr + "T23:59:59").getTime() / 1000)
}

function formatDateTime(epochTime, lang) {
    const locale = lang === "it" ? it : enUS
    return format(fromUnixTime(epochTime), "dd/MM/yyyy HH:mm:ss", { locale })
}

function Pagination({ currentPage, totalPages, onPageChange }) {
    if (totalPages <= 1) return null
    return (
        <nav>
            <ul className="pagination pagination-sm justify-content-center mb-0">
                <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => onPageChange(currentPage - 1)}>‹</button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => i).map(i => (
                    <li key={i} className={`page-item ${i === currentPage ? "active" : ""}`}>
                        <button className="page-link" onClick={() => onPageChange(i)}>{i + 1}</button>
                    </li>
                ))}
                <li className={`page-item ${currentPage === totalPages - 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => onPageChange(currentPage + 1)}>›</button>
                </li>
            </ul>
        </nav>
    )
}

function AttendanceArchive() {
    const [t, i18n] = useTranslation(["global", "attendance"])

    const [page, setPage] = useState(0)
    const [fromDate, setFromDate] = useState("")
    const [toDate, setToDate] = useState("")
    const [nameFilter, setNameFilter] = useState("")
    const [selectedBu, setSelectedBu] = useState("ALL")

    const [appliedFrom, setAppliedFrom] = useState(undefined)
    const [appliedTo, setAppliedTo] = useState(undefined)
    const [appliedBu, setAppliedBu] = useState(undefined)

    const { data: businessUnits = [] } = useGetBusinessUnitsQuery()

    const { data: response, isFetching, isLoading, isSuccess, isError, error, refetch } =
        useGetBadgeEventsPagedQuery({
            page,
            size: PAGE_SIZE,
            fromEpoch: appliedFrom,
            toEpoch: appliedTo,
            businessUnitGuid: appliedBu
        })

    const handleApplyFilters = () => {
        setAppliedFrom(dateStringToEpoch(fromDate))
        setAppliedTo(dateStringToEpochEnd(toDate))
        setAppliedBu(selectedBu === "ALL" ? undefined : selectedBu)
        setPage(0)
    }

    const handleClearFilters = () => {
        setFromDate("")
        setToDate("")
        setNameFilter("")
        setSelectedBu("ALL")
        setAppliedFrom(undefined)
        setAppliedTo(undefined)
        setAppliedBu(undefined)
        setPage(0)
    }

    const isAnyFilterApplied = () =>
        appliedFrom != null || appliedTo != null || appliedBu != null || nameFilter.trim() !== ""

    const filteredEvents = useMemo(() => {
        if (!response?.content) return []
        if (!nameFilter.trim()) return response.content
        const q = nameFilter.toLowerCase()
        return response.content.filter(e =>
            (e.user?.displayName ?? e.userGuid).toLowerCase().includes(q)
        )
    }, [response, nameFilter])

    const content = (
        <>
            {/* Filters — SafetyDashboard style */}
            <div className="d-flex gap-3 mb-4 align-items-end flex-wrap">
                <label>
                    {t("attendance:label.business_unit")}:
                    <select
                        className="form-select"
                        value={selectedBu}
                        onChange={e => setSelectedBu(e.target.value)}
                    >
                        <option value="ALL">{t("particles.any")}</option>
                        {businessUnits.map(bu => (
                            <option key={bu.businessUnit.guid} value={bu.businessUnit.guid}>{bu.businessUnit.name}</option>
                        ))}
                    </select>
                </label>

                <label>
                    {t("attendance:filter.from_date")}:
                    <input
                        type="date"
                        className="form-control"
                        value={fromDate}
                        onChange={e => setFromDate(e.target.value)}
                    />
                </label>

                <label>
                    {t("attendance:filter.to_date")}:
                    <input
                        type="date"
                        className="form-control"
                        value={toDate}
                        onChange={e => setToDate(e.target.value)}
                    />
                </label>

                <label>
                    {t("attendance:filter.user_name")}:
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Es. Rossi"
                        value={nameFilter}
                        onChange={e => setNameFilter(e.target.value)}
                    />
                </label>

                <button className="btn btn-primary" onClick={handleApplyFilters}>
                    {t("attendance:filter.apply")}
                </button>

                {isAnyFilterApplied() && (
                    <button className="btn btn-primary" onClick={handleClearFilters}>
                        <i className="fa-solid fa-xmark" /> {t("action.clear_filter")}
                    </button>
                )}
            </div>

            {isAnyFilterApplied() && <WarningMessage message={t("helper.filter_applied_warning")} />}

            {/* Table */}
            {isSuccess && (
                <>
                    <div className="text-muted small mb-2">
                        {response.totalElements} {t("attendance:label.recent_events").toLowerCase()}
                    </div>

                    {filteredEvents.length === 0 ? (
                        <div className="text-center text-muted py-5">
                            <i className="fa-solid fa-magnifying-glass fa-2x mb-3 d-block opacity-25" />
                            {t("attendance:helper.no_events")}
                        </div>
                    ) : (
                        <table className="table table-hover table-striped align-middle">
                            <thead className="table-dark">
                                <tr>
                                    <th style={{ width: "90px" }}>{t("attendance:label.event_type")}</th>
                                    <th>{t("attendance:label.user")}</th>
                                    <th>{t("attendance:label.business_unit")}</th>
                                    <th>{t("attendance:label.date_time")}</th>
                                    <th style={{ width: "100px" }} />
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEvents.map(event => {
                                    const isEntry = event.eventType === "ENTRY"
                                    return (
                                        <tr key={event.guid}>
                                            <td>
                                                <span className={`badge ${isEntry ? "bg-success" : "bg-primary"}`}>
                                                    {isEntry ? "→ " : "← "}
                                                    {isEntry
                                                        ? t("attendance:event.entry")
                                                        : t("attendance:event.exit")}
                                                </span>
                                            </td>
                                            <td>
                                                {event.user
                                                    ? <AvatarMini guid={event.user.guid} displayName={event.user.displayName} abbreviate={true} size={32} />
                                                    : <span className="fw-semibold text-muted">{event.userGuid}</span>
                                                }
                                            </td>
                                            <td className="text-muted small">
                                                {event.businessUnit?.name ?? "—"}
                                            </td>
                                            <td className="text-muted small">
                                                {formatDateTime(event.epochTime, i18n.language)}
                                            </td>
                                            <td>
                                                {event.syncedAt != null && (
                                                    <span className="badge bg-warning text-dark small">
                                                        {t("attendance:helper.queue_label")}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    )}

                    <Pagination
                        currentPage={page}
                        totalPages={response.totalPages}
                        onPageChange={setPage}
                    />
                </>
            )}
        </>
    )

    return (
        <PageLayout
            pageTitle={t("attendance:menu.archive")}
            bodyContent={content}
            isLoadingPage={isLoading}
            isLoadingContent={isFetching}
            isError={isError}
            errorMsg={error}
            onPageRefresh={refetch}
        />
    )
}

export default AttendanceArchive
